import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, FileText, Calendar, Target, Play, ArrowRight } from "lucide-react";

const GroupStudy = () => {
  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Team Coordination",
      description: "Organize study groups for classes, projects, or exam prep",
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Shared Resources",
      description: "Upload notes, documents, and study materials for everyone",
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Session Scheduling",
      description: "Plan study sessions and send reminders to your group",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Group Goals",
      description: "Set collective targets and track progress together",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Group Study - Collaborative Online Study Sessions"
        description="Create collaborative study groups online. Share resources, schedule sessions, set group goals, and stay motivated with your study partners."
        keywords="group study, study group online, collaborative study, study together, team study, study partners"
        canonicalUrl="/group-study/"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Users className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">COLLABORATIVE LEARNING</span>
              </div>
              
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-gradient">Group Study Sessions</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Collaborate, learn, and succeed together. Perfect for study groups, class projects, and exam preparation teams.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Create Study Group <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
              
              {/* Video Section */}
              <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full aspect-video object-cover"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                      <Play className="w-5 h-5 text-primary-foreground ml-1" />
                    </div>
                    <span className="text-foreground font-medium">See how it works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                  Built for <span className="text-gradient">Collaboration</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  All the tools you need to study effectively as a team
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <CardHeader>
                      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {feature.icon}
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-6">
                Perfect For Every <span className="text-gradient">Scenario</span>
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Research shows group studying with cameras on significantly boosts focus and accountability. <a href="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline">Learn the science behind why it works →</a>
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { 
                    title: "Exam Preparation", 
                    desc: "Form study groups for upcoming exams. Share review materials, quiz each other, and stay motivated during intense study periods.",
                    items: ["Coordinate review sessions", "Share practice questions", "Divide and conquer topics"]
                  },
                  { 
                    title: "Class Projects", 
                    desc: "Manage group assignments from start to finish. Keep everyone on the same page with shared goals and regular check-ins.",
                    items: ["Assign roles and tasks", "Track individual progress", "Meet deadlines together"]
                  },
                  { 
                    title: "Study Buddies", 
                    desc: "Create small accountability groups for ongoing coursework. Regular sessions keep everyone consistent and engaged.",
                    items: ["Weekly study sessions", "Compare notes and insights", "Support each other's learning"]
                  },
                  { 
                    title: "Learning Circles", 
                    desc: "Build communities around shared interests or subjects. Perfect for self-directed learning and skill development.",
                    items: ["Explore topics together", "Share resources and tutorials", "Grow your knowledge collectively"]
                  },
                ].map((item, index) => (
                  <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all">
                    <h3 className="text-2xl font-bold text-foreground mb-4">{item.title}</h3>
                    <p className="text-muted-foreground mb-6">{item.desc}</p>
                    <ul className="space-y-2">
                      {item.items.map((listItem, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-muted-foreground">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          {listItem}
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                Start Your <span className="text-gradient">Group Study</span>
              </h2>
              <div className="space-y-8">
                {[
                  { step: "1", title: "Create Your Group", desc: "Set up a dedicated study group with a name, description, and schedule" },
                  { step: "2", title: "Invite Members", desc: "Share your group code with classmates or friends who want to join" },
                  { step: "3", title: "Collaborate & Achieve", desc: "Work together, share resources, and reach your academic goals as a team" },
                ].map((item, index) => (
                  <div key={index} className="flex gap-6 items-start animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center font-bold text-2xl text-primary-foreground glow-primary">
                      {item.step}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-2xl font-semibold mb-2 text-foreground">{item.title}</h3>
                      <p className="text-muted-foreground text-lg">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Achieve More <span className="text-gradient">Together</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create your study group today and unlock the power of collaborative learning.
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Create Group Now
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GroupStudy;